// "New" — the Home rail listing the clips that entered the library most
// recently, straight from each technique's createdAt. The content-news
// counterpart to Popular Now: what just landed, rather than what everyone is
// starring.
//
// Hidden until something carries a date: seeded clips share the seed date, so
// with nothing added since there is no "new" to show.
import { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { useTechniques } from '../hooks/useTechniques';
import TechniqueTile from './TechniqueTile';
import Rail from './Rail';
import { TIER_ACCENT_COLORS } from '../constants/layout';
import { groupTechniques } from '../utils/groupTechniques';
import { TechniqueStatus, type BeltRank } from '../types';
import { TechniqueGrid, type LibraryBridge } from './Spotlights';
import { DetailPage } from './Forum';
import { FeedSkeleton, TileRailSkeleton } from './RailSkeleton';

/** How many tiles the rail carries, matching Popular Now. */
const SHOWN = 10;

/** How many the full-screen grid goes back through. */
const ALL = 60;

export default function Newest({ library, feed, open, onOpen, onClose }: {
  library?: LibraryBridge;
  /** Render this many clips into Home's four-across feed instead of the rail. */
  feed?: number;
  /** Render the full-screen grid of the last ALL clips instead of the rail. */
  open?: boolean;
  /** The rail's "See all" — Home flips `open` (it hides the other rails). */
  onOpen?: () => void;
  onClose?: () => void;
}) {
  const { techniques, loading } = useTechniques();

  // Newest first, then a gi/no-gi pair counts as one entry, the way Library
  // shows them (groupTechniques keeps the order, so the group sits where its
  // newest clip sat).
  const dated = useMemo(() =>
    techniques
      .filter((t) => t.createdAt && t.instagramUrl)
      .sort((a, b) => b.createdAt! - a.createdAt!),
    [techniques]);
  const rows = useMemo(() => groupTechniques(dated).slice(0, SHOWN), [dated]);

  // Home's feed: the same clips, flowing down the page rather than off to the
  // right, as many as the page has asked for so far.
  if (feed !== undefined) {
    if (loading) return <FeedSkeleton />;
    if (dated.length === 0) return <div className="home-feed-empty">Nothing new yet.</div>;
    return <TechniqueGrid list={dated.slice(0, feed)} library={library} className="home-grid" />;
  }

  if (open) {
    return (
      <DetailPage title="New" icon={<Clock size={17} />} onBack={() => onClose?.()}>
        <div className="sp-scroll">
          <TechniqueGrid list={dated.slice(0, ALL)} library={library} />
        </div>
      </DetailPage>
    );
  }

  // Still fetching the catalog: hold the rail's space rather than opening
  // on a blank page and popping the strip in.
  if (loading) return <TileRailSkeleton title="New" icon={<Clock size={16} />} />;
  if (rows.length === 0) return null;

  return (
    <Rail title="New" icon={<Clock size={16} />} onSeeAll={onOpen} tiles>
      {rows.map((g) => { const t = g.lead; return (
        <div className="rail-cell" key={g.key}>
          <TechniqueTile
            id={t.id}
            name={t.name}
            status={library?.statusOf(t.id) ?? TechniqueStatus.Unchecked}
            categoryColor={TIER_ACCENT_COLORS[t.tier as BeltRank]}
            instagramUrl={t.instagramUrl}
            thumbnail={t.thumbnail}
            watched={library?.isWatched(t) ?? false}
            onPipClick={library ? () => library.onPlay(t) : undefined}
            onToggleMat={library ? () => library.onToggleMat(t) : undefined}
            onInfoClick={library ? () => library.onInfo(t) : undefined}
            variantCount={g.variants.length}
            onVariantsClick={library ? () => library.onVersions(g) : undefined}
            onOpenAllVersions={library ? () => library.onOpenAllVersions(g) : undefined}
          />
        </div>
      ); })}
    </Rail>
  );
}
